import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ClienteCache, clienteDisplayName } from "@/services/clienteCache";
import { BackButton } from "@/components/ui/back-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Bell, FileText, Building2, Users, CalendarIcon, Trash2 } from "lucide-react";
import {
  type ObraRecord,
  listObras,
  hydrateObras,
  deleteObra as cacheDeleteObra,
} from "@/lib/obraCache";
import { useToast } from "@/hooks/use-toast";
import { getClienteNomeById } from "@/utils/clientesData";
import { getProgressoObra } from "@/services/obraProgress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { WorksFilters } from "@/components/works/WorksFilters";
import { WorksGrid } from "@/components/works/WorksGrid";
import { NovaObraModal } from "@/components/obras";
import { PreencherRelatorioModal } from "@/components/rdo/PreencherRelatorioModal";

export type WorkStatus = "em-andamento" | "paralisada" | "concluida";

export interface Work {
  id: string;
  name: string;
  client: string;
  /** 0–100, ou null quando não há tarefas para medir o avanço. */
  progress: number | null;
  tarefasConcluidas: number;
  tarefasTotal: number;
  daysRemaining: number;
  lastActivity: string;
  status: WorkStatus;
  city: string;
  expectedEndDate: string; // Data de previsão de término
}

interface ContatoBuscavel {
  id: string;
  name: string;
  email: string;
  type: "cliente" | "fornecedor";
  phone: string;
}

// Function to map ObraRecord to Work interface
const mapObraToWork = (obra: ObraRecord): Work => {
  const today = new Date();
  const endDate = obra.previsaoTermino ? new Date(obra.previsaoTermino) : null;
  
  // daysRemaining continua vindo do prazo (para exibir dias restantes/atraso),
  // mas NÃO define o status.
  const daysRemaining = endDate
    ? Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 3600 * 24))
    : 0;

  // Status vem do statusObra PERSISTIDO (mesma regra do WorksSummary/Dashboard).
  // Antes era derivado do prazo, o que divergia do Dashboard e quebrava o filtro.
  const mapStatus = (s?: string | null): WorkStatus => {
    switch (s) {
      case "concluido":
        return "concluida";
      case "pausado":
        return "paralisada";
      default: // "em-andamento", "planejamento" ou vazio contam como ativa
        return "em-andamento";
    }
  };
  const status = mapStatus(obra.statusObra);
  // Progresso real: 100% quando a obra está concluída (o status é a fonte de
  // verdade nesse caso); nas demais, o percentual de tarefas concluídas. Obra
  // sem tarefas devolve null — a UI mostra "sem tarefas" em vez de um número
  // inventado (antes toda obra ativa exibia 50% fixo).
  const { percentual, concluidas, total } = getProgressoObra(obra.id);
  const progress = status === "concluida" ? 100 : percentual;
  
  return {
    id: obra.id,
    name: obra.nomeObra || "Obra sem nome",
    client: obra.clienteObra ? getClienteNomeById(obra.clienteObra) : "Cliente não informado",
    progress,
    tarefasConcluidas: concluidas,
    tarefasTotal: total,
    daysRemaining,
    lastActivity: obra.updatedAt ? new Date(obra.updatedAt).toISOString().split('T')[0] : today.toISOString().split('T')[0],
    status,
    city: obra.endereco?.cidade || "Cidade não informada",
    expectedEndDate: obra.previsaoTermino ? (typeof obra.previsaoTermino === 'string' ? obra.previsaoTermino : obra.previsaoTermino.toISOString().split('T')[0]) : ""
  };
};

const Works = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<WorkStatus | "todas">("todas");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isNovaObraModalOpen, setIsNovaObraModalOpen] = useState(false);
  const [isRelatorioModalOpen, setIsRelatorioModalOpen] = useState(false);
  const [isNovoUsuarioModalOpen, setIsNovoUsuarioModalOpen] = useState(false);
  const [isUserSearchOpen, setIsUserSearchOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Novo usuário form states
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userProfile, setUserProfile] = useState("");
  const [userStatus, setUserStatus] = useState("ativo");
  const [approveReport, setApproveReport] = useState(false);
  const [viewReportLevel, setViewReportLevel] = useState("");
  const [permissions, setPermissions] = useState({
    usuariosSistema: { add: false, edit: false, view: false },
    obra: { add: false, edit: false, view: false },
    relatorios: { add: false, edit: false, view: false },
    gestorTarefas: { add: false, edit: false, view: false },
    relatoriosGerenciais: { add: false, edit: false, view: false },
    obraIA: { add: false, edit: false, view: false },
    clientes: { add: false, edit: false, view: false },
    materiais: { add: false, edit: false, view: false },
    equipamentos: { add: false, edit: false, view: false },
    horarioTrabalho: { add: false, edit: false, view: false },
    condicaoClimatica: { add: false, edit: false, view: false },
    maoObra: { add: false, edit: false, view: false },
    atividade: { add: false, edit: false, view: false },
    ocorrencia: { add: false, edit: false, view: false },
    checklist: { add: false, edit: false, view: false },
    controleMaterial: { add: false, edit: false, view: false },
    comentario: { add: false, edit: false, view: false },
    foto: { add: false, edit: false, view: false },
    video: { add: false, edit: false, view: false },
    anexo: { add: false, edit: false, view: false }
  });

  // Restore filters from URL params on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const search = urlParams.get('search');
    const status = urlParams.get('status') as WorkStatus | "todas";
    const view = urlParams.get('view') as "grid" | "list";

    if (search) setSearchTerm(search);
    if (status) setSelectedStatus(status);
    if (view) setViewMode(view);
  }, [location.search]);

  // Update URL when filters change
  const updateURL = (newSearchTerm: string, newStatus: WorkStatus | "todas", newViewMode: "grid" | "list") => {
    const params = new URLSearchParams();
    if (newSearchTerm) params.set('search', newSearchTerm);
    if (newStatus !== 'todas') params.set('status', newStatus);
    if (newViewMode !== 'grid') params.set('view', newViewMode);
    
    const queryString = params.toString();
    navigate(`/obras${queryString ? `?${queryString}` : ''}`, { replace: true });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    updateURL(value, selectedStatus, viewMode);
  };

  const handleStatusChange = (status: WorkStatus | "todas") => {
    setSelectedStatus(status);
    updateURL(searchTerm, status, viewMode);
  };

  const handleViewModeChange = (mode: "grid" | "list") => {
    setViewMode(mode);
    updateURL(searchTerm, selectedStatus, mode);
  };

  // Fonte única: obras do obraCache (hidratado do Supabase no login).
  // O refreshTrigger relê o cache (o NovaObraModal re-hidrata após salvar).
  const [supabaseObras, setSupabaseObras] = useState<ObraRecord[]>(() => listObras());
  const { toast } = useToast();

  useEffect(() => {
    setSupabaseObras(listObras());
  }, [refreshTrigger]);

  // Fonte única: obras reais do Supabase (sem dados de exemplo)
  const allWorks = useMemo(
    () => supabaseObras.map(mapObraToWork),
    [supabaseObras]
  );

  // Filtrar obras baseado na busca e status
  const filteredWorks = allWorks.filter(work => {
    const matchesSearch = work.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         work.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         work.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = selectedStatus === "todas" || work.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Contatos buscáveis: clientes reais do cadastro (tabela `clientes`, hidratada
  // no login). Antes eram 10 nomes fictícios hardcoded — "João Silva",
  // "Ricardo Materiais Ltda" — que apareciam para o usuário final.
  // Fornecedor ainda não entra aqui: não existe tabela `fornecedores` no banco.
  const contatosBuscaveis = useMemo<ContatoBuscavel[]>(
    () =>
      ClienteCache.getClientes().map((c) => ({
        id: c.id,
        name: clienteDisplayName(c),
        email: c.emails[0] ?? "",
        type: "cliente" as const,
        phone: c.telefones[0] ?? "",
      })),
    []
  );

  // Filter clients and suppliers based on search term
  const filteredClientsAndSuppliers = contatosBuscaveis.filter(item =>
    item.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    item.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // Function to select a user from the search results
  const selectUser = (user: ContatoBuscavel) => {
    setUserName(user.name);
    setUserEmail(user.email);
    setUserPhone(user.phone);
    setIsUserSearchOpen(false);
    setUserSearchTerm("");
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="pt-4 px-4 sm:px-6 space-y-6">
        {/* Header simplificado */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-6 min-w-0">
            <BackButton />
            <h1 className="text-2xl font-semibold text-foreground truncate">Obras</h1>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="h-4 w-4 mr-2" />
                Adicionar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => setIsRelatorioModalOpen(true)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Preencher RDO
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => setIsNovaObraModalOpen(true)}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Adicionar Obra
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer"
                onClick={() => setIsNovoUsuarioModalOpen(true)}
              >
                <Users className="h-4 w-4 mr-2" />
                Adicionar Usuário
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer text-destructive"
                onClick={async () => {
                  if (
                    confirm(
                      "Tem certeza que deseja deletar todas as suas obras? Esta ação não pode ser desfeita."
                    )
                  ) {
                    // Remove via obraCache (write-through no Supabase) e
                    // re-hidrata para confirmar o estado do banco.
                    supabaseObras.forEach((o) => cacheDeleteObra(o.id));
                    setRefreshTrigger((prev) => prev + 1);
                    await hydrateObras();
                    setRefreshTrigger((prev) => prev + 1);
                    toast({ title: "Obras removidas" });
                  }
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Limpar Obras
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Filtros com busca integrada */}
        <WorksFilters 
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          works={allWorks}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        {/* Grade de Obras */}
        <WorksGrid works={filteredWorks} viewMode={viewMode} />
      </div>

      {/* Modals */}
      <NovaObraModal 
        open={isNovaObraModalOpen}
        onOpenChange={setIsNovaObraModalOpen}
        onSubmit={() => {
          // Refresh works list when obra is created
          setRefreshTrigger(prev => prev + 1);
          setIsNovaObraModalOpen(false);
        }}
      />

      {/* Preencher Relatório Modal */}
      <PreencherRelatorioModal
        open={isRelatorioModalOpen}
        onOpenChange={setIsRelatorioModalOpen}
      />

      {/* Novo Usuário Dialog */}
      <Dialog open={isNovoUsuarioModalOpen} onOpenChange={setIsNovoUsuarioModalOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Usuário</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Informações Básicas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="userName">Nome do usuário <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <Input
                    id="userName"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Digite o nome completo"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    size="icon"
                    onClick={() => setIsUserSearchOpen(true)}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userEmail">E-mail <span className="text-red-500">*</span></Label>
                <Input
                  id="userEmail"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="Digite o e-mail"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="userRole">Cargo</Label>
                <Input
                  id="userRole"
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  placeholder="Digite o cargo"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userPhone">Telefone</Label>
                <Input
                  id="userPhone"
                  value={userPhone}
                  onChange={(e) => setUserPhone(e.target.value)}
                  placeholder="Digite o telefone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Senha</Label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                    placeholder="Digite a senha"
                  />
                  <Button variant="outline" type="button">
                    Gerar Link
                  </Button>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Perfil</Label>
                <Select value={userProfile} onValueChange={(value) => {
                  setUserProfile(value);
                  
                  // Pre-configure permissions for "Cliente" profile
                  if (value === "cliente") {
                    setPermissions({
                      usuariosSistema: { add: false, edit: false, view: true },
                      obra: { add: false, edit: false, view: true },
                      relatorios: { add: false, edit: false, view: true },
                      gestorTarefas: { add: false, edit: false, view: false },
                      relatoriosGerenciais: { add: false, edit: false, view: false },
                      obraIA: { add: false, edit: false, view: false },
                      clientes: { add: false, edit: false, view: false },
                      materiais: { add: false, edit: false, view: true },
                      equipamentos: { add: false, edit: false, view: false },
                      horarioTrabalho: { add: false, edit: false, view: true },
                      condicaoClimatica: { add: false, edit: false, view: true },
                      maoObra: { add: false, edit: false, view: true },
                      atividade: { add: false, edit: false, view: true },
                      ocorrencia: { add: false, edit: false, view: false },
                      checklist: { add: false, edit: false, view: false },
                      controleMaterial: { add: false, edit: false, view: false },
                      comentario: { add: false, edit: false, view: true },
                      foto: { add: false, edit: false, view: true },
                      video: { add: false, edit: false, view: true },
                      anexo: { add: false, edit: false, view: false }
                    });
                  } else if (value === "administrador") {
                    // Set all permissions for administrator
                    setPermissions(Object.keys(permissions).reduce((acc, key) => ({
                      ...acc,
                      [key]: { add: true, edit: true, view: true }
                    }), {} as typeof permissions));
                  } else if (value === "personalizado") {
                    // Set specific permissions for "personalizado" profile
                    setPermissions({
                      usuariosSistema: { add: false, edit: true, view: true },
                      obra: { add: true, edit: true, view: true },
                      relatorios: { add: true, edit: true, view: true },
                      gestorTarefas: { add: true, edit: true, view: true },
                      relatoriosGerenciais: { add: true, edit: true, view: true },
                      obraIA: { add: true, edit: true, view: true },
                      clientes: { add: true, edit: true, view: true },
                      materiais: { add: true, edit: true, view: true },
                      equipamentos: { add: true, edit: true, view: true },
                      horarioTrabalho: { add: true, edit: true, view: true },
                      condicaoClimatica: { add: true, edit: true, view: true },
                      maoObra: { add: true, edit: true, view: true },
                      atividade: { add: true, edit: true, view: true },
                      ocorrencia: { add: true, edit: true, view: true },
                      checklist: { add: true, edit: true, view: true },
                      controleMaterial: { add: true, edit: true, view: true },
                      comentario: { add: true, edit: true, view: true },
                      foto: { add: true, edit: true, view: true },
                      video: { add: true, edit: true, view: true },
                      anexo: { add: true, edit: true, view: true }
                     });
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administrador">Administrador</SelectItem>
                    <SelectItem value="cliente">Cliente</SelectItem>
                    <SelectItem value="personalizado">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select value={userStatus} onValueChange={setUserStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Poderá visualizar relatório</Label>
                <Select value={viewReportLevel} onValueChange={setViewReportLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o nível" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="preenchendo">Preenchendo relatório</SelectItem>
                    <SelectItem value="aguardando">Aguardando aprovação</SelectItem>
                    <SelectItem value="aprovado">Aprovado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="approveReport"
                checked={approveReport}
                onCheckedChange={setApproveReport}
              />
              <Label htmlFor="approveReport">Aprovar relatório</Label>
            </div>

            {/* Tabela de Permissões */}
            <div className="grid gap-2">
              <Label>Permissões</Label>
              <div className="border rounded-md p-4">
                <div className="grid grid-cols-4 gap-4 mb-4 pb-2 border-b">
                  <Label className="font-semibold">Descrição</Label>
                  <Label className="font-semibold text-center">Adicionar</Label>
                  <Label className="font-semibold text-center">Editar</Label>
                  <Label className="font-semibold text-center">Visualizar</Label>
                </div>
                
                {Object.entries({
                  usuariosSistema: "Usuários do sistema",
                  obra: "Obra",
                  relatorios: "Relatórios",
                  gestorTarefas: "Gestor de Tarefas",
                  relatoriosGerenciais: "Relatórios Gerenciais",
                  obraIA: "Obra IA",
                  clientes: "Clientes",
                  materiais: "Materiais",
                  equipamentos: "Equipamentos",
                  horarioTrabalho: "Horário de trabalho",
                  condicaoClimatica: "Condição Climática",
                  maoObra: "Mão de Obra",
                  atividade: "Atividade",
                  ocorrencia: "Ocorrência",
                  checklist: "Checklist",
                  controleMaterial: "Controle de Material",
                  comentario: "Comentário",
                  foto: "Foto",
                  video: "Vídeo",
                  anexo: "Anexo"
                }).map(([key, label]) => (
                  <div key={key} className="grid grid-cols-4 gap-4 py-2 items-center">
                    <Label className="text-sm">{label}</Label>
                    <div className="flex justify-center">
                      <Checkbox
                        checked={permissions[key as keyof typeof permissions]?.add || false}
                        onCheckedChange={(checked) => 
                          setPermissions(prev => ({
                            ...prev,
                            [key]: { ...prev[key as keyof typeof permissions], add: checked as boolean }
                          }))
                        }
                      />
                    </div>
                    <div className="flex justify-center">
                      <Checkbox
                        checked={permissions[key as keyof typeof permissions]?.edit || false}
                        onCheckedChange={(checked) => 
                          setPermissions(prev => ({
                            ...prev,
                            [key]: { ...prev[key as keyof typeof permissions], edit: checked as boolean }
                          }))
                        }
                      />
                    </div>
                    <div className="flex justify-center">
                      <Checkbox
                        checked={permissions[key as keyof typeof permissions]?.view || false}
                        onCheckedChange={(checked) => 
                          setPermissions(prev => ({
                            ...prev,
                            [key]: { ...prev[key as keyof typeof permissions], view: checked as boolean }
                          }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsNovoUsuarioModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                // TODO: Implementar salvamento do usuário
                setIsNovoUsuarioModalOpen(false);
              }}
            >
              Salvar Usuário
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Search Dialog */}
      <Dialog open={isUserSearchOpen} onOpenChange={setIsUserSearchOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Buscar Usuário</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="userSearch">Buscar por nome ou e-mail</Label>
              <Input
                id="userSearch"
                placeholder="Digite o nome ou e-mail..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="border rounded-md max-h-[400px] overflow-y-auto">
              {filteredClientsAndSuppliers.length > 0 ? (
                <div className="divide-y">
                  {filteredClientsAndSuppliers.map((user) => (
                    <div
                      key={user.id}
                      className="p-4 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => selectUser(user)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          <div className="text-sm text-muted-foreground">{user.phone}</div>
                        </div>
                        <div className="text-xs bg-secondary px-2 py-1 rounded-md">
                          {user.type === "cliente" ? "Cliente" : "Fornecedor"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  {userSearchTerm ? "Nenhum usuário encontrado" : "Digite para buscar usuários"}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsUserSearchOpen(false);
                setUserSearchTerm("");
              }}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Works;
